import { useState, useEffect, useCallback, useRef } from 'react';
import { Property } from '../types';
import { get183150PropertiesPaginatedApi, savePropertyApi } from '../services/adminPropertyApiClient';
import { fetchInmobiliariasConfigApi } from '../services/configApiClient';
import { ensureSpecialNegotiationInOwners } from '../utils/operationOwnerFilterHelper';

const PAGE_SIZE = 100;

export interface SpreadsheetFilters {
  status: string[];
  owners: string[];
  brpCode: string;
  propertyCode: string;
}

export const useAdminSpreadsheet = (isActive: boolean) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [allowedOwners, setAllowedOwners] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<SpreadsheetFilters>({
    status: [],
    owners: [],
    brpCode: '',
    propertyCode: '',
  });

  const fetchOwners = useCallback(async () => {
    try {
      const inmoData = await fetchInmobiliariasConfigApi();
      const ownersSet = new Set<string>();
      const doc183150 = inmoData['183150'] || {};
      Object.keys(doc183150).forEach((k) => {
        if (typeof doc183150[k] === 'object' && k !== 'id') {
          ownersSet.add(k);
        }
      });
      setAllowedOwners(ensureSpecialNegotiationInOwners(Array.from(ownersSet)));
    } catch (e) {
      console.error('Error fetching owners:', e);
    }
  }, []);

  const loadInitial183150 = useCallback(async () => {
    if (!isActive) return;
    setLoading(true);
    setError(null);
    try {
      await fetchOwners();
      const statusParam = filters.status[0] || undefined;
      const ownerParam = filters.owners[0] || undefined;
      const res = await get183150PropertiesPaginatedApi(0, PAGE_SIZE, {
        status: statusParam,
        owner: ownerParam,
        brpCode: filters.brpCode || undefined,
        propertyCode: filters.propertyCode || undefined,
      });
      setProperties(res.items || []);
      setHasMore(res.hasMore);
    } catch (err: any) {
      console.error('Error loading 183150 properties:', err);
      setError(err?.message || 'Error al cargar inventario 183150');
    } finally {
      setLoading(false);
    }
  }, [isActive, fetchOwners, filters]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const statusParam = filters.status[0] || undefined;
      const ownerParam = filters.owners[0] || undefined;
      const res = await get183150PropertiesPaginatedApi(properties.length, PAGE_SIZE, {
        status: statusParam,
        owner: ownerParam,
        brpCode: filters.brpCode || undefined,
        propertyCode: filters.propertyCode || undefined,
      });
      setProperties((prev) => [...prev, ...(res.items || [])]);
      setHasMore(res.hasMore);
    } catch (err: any) {
      console.error('Error loading more 183150 items:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [loading, loadingMore, hasMore, properties.length, filters]);

  useEffect(() => {
    loadInitial183150();
  }, [loadInitial183150]);

  const updateProperty = async (id: string, updates: Partial<Property>) => {
    try {
      await savePropertyApi(id, updates);
      setProperties((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      return true;
    } catch (err: any) {
      console.error('Update error:', err);
      return false;
    }
  };

  const updateFilters = (newFilters: Partial<SpreadsheetFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const fetchAllFilteredProperties = async (): Promise<Property[]> => {
    const statusParam = filters.status[0] || undefined;
    const ownerParam = filters.owners[0] || undefined;
    const res = await get183150PropertiesPaginatedApi(0, 10000, {
      status: statusParam,
      owner: ownerParam,
      brpCode: filters.brpCode || undefined,
      propertyCode: filters.propertyCode || undefined,
    });
    return res.items || [];
  };

  return {
    properties,
    allowedOwners,
    loading,
    loadingMore,
    error,
    hasMore,
    filters,
    updateFilters,
    refresh: loadInitial183150,
    updateProperty,
    loadMore,
    fetchAllFilteredProperties,
  };
};
